import ko from 'knockout';
import {customFetch as fetch} from "../../../fetch";
import moment from "moment";


class AfasDashboardBaseCompany {
    name : KnockoutObservable<string>;
    companyId : KnockoutObservable<string>;
    employeesCount : KnockoutObservable<number>;
    clockedUnitCount : KnockoutObservable<number>;
    totalHours : KnockoutObservable<number>;
    longestClock : KnockoutObservable<number>;
    shortestClock : KnockoutObservable<number>;
    parent: KnockoutObservable<AfasDashboardModel>;
    sending: KnockoutObservable<boolean>;
    
    totalHoursText: KnockoutComputed<string>;
    shortestLongestText: KnockoutComputed<string>;
    
    constructor(model: any, parent: AfasDashboardModel) {
        this.parent = ko.observable(parent);
        this.name = ko.observable(model.name);
        this.companyId = ko.observable(model.companyId);
        this.employeesCount = ko.observable(model.employeesCount);
        this.clockedUnitCount = ko.observable(model.clockedUnitCount);
        this.totalHours = ko.observable(model.totalHours);
        this.longestClock = ko.observable(model.longestClock);
        this.shortestClock = ko.observable(model.shortestClock);
        this.sending = ko.observable(false);
        this.totalHoursText = ko.pureComputed(() => `${this.totalHours().toFixed(2)} uur`);
        this.shortestLongestText = ko.pureComputed(() => `Kortste / Langste: ${this.shortestClock().toFixed(2)} / ${this.longestClock().toFixed(2)}`);
    }
}

class AfasDashboardBaseEmployee {
    id : KnockoutObservable<string>;
    fullName : KnockoutObservable<string>;
    afasEmployeeId: KnockoutObservable<string>;
    hourlyWage: KnockoutObservable<number>;
    clockedUnitCount : KnockoutObservable<number>;
    totalHours : KnockoutObservable<number>;
    parent: KnockoutObservable<AfasDashboardModel>;
    formattedHourlyWage: KnockoutObservable<string>;
    contractsUrl: KnockoutObservable<string>;
    error: KnockoutObservable<string>;
    sending: KnockoutObservable<boolean>;

    totalHoursText: KnockoutComputed<string>;
    
    constructor(model: any, parent: AfasDashboardModel) {
        this.parent = ko.observable(parent);
        this.id = ko.observable(model.id);
        this.fullName = ko.observable(model.fullName);
        this.clockedUnitCount = ko.observable(model.clockedUnitCount);
        this.hourlyWage = ko.observable(model.hourlyWage);
        this.totalHours = ko.observable(model.totalHours);
        this.afasEmployeeId = ko.observable(model.afasEmployeeId);
        this.sending = ko.observable(false);
        this.formattedHourlyWage = ko.pureComputed(() => `€${this.hourlyWage().toFixed(2)}`);
        this.contractsUrl = ko.pureComputed(() => `/Admin/Employee/Contracts/${this.id()}`);
        this.error = ko.observable(model.error);
        this.totalHoursText = ko.pureComputed(() => `${this.totalHours().toFixed(2)} uur`);

    }
}


class HoursCompany extends AfasDashboardBaseCompany {

    name: KnockoutObservable<string>;
    employees : KnockoutObservable<HoursEmployee[]>;
    branches: KnockoutObservable<HoursBranch[]>;
    
    
    constructor(model: any, parent: AfasDashboardModel) {
        super(model, parent);

        this.name = ko.observable<string>(model.name);
        this.employees = ko.observable<HoursEmployee[]>([]);
        this.branches = ko.observable<HoursBranch[]>([]);
    }
    
    async getBranches() {
        await this.getBranchesFromUrl(this.parent().options().getHoursBranchesUrl);
    }

    async getErrorBranches() {
        await this.getBranchesFromUrl(this.parent().options().getErrorBranchesUrl);
    }
    
    async getBranchesFromUrl(url: string) {
        const fd = new FormData();
        fd.append('companyId',this.companyId());
        if(this.parent().startDate()) {
            fd.append('startDate', this.parent().startDate().format('YYYY-MM-DD HH:mm'));
        }
        if(this.parent().endDate()) {
            fd.append('endDate', this.parent().endDate().format('YYYY-MM-DD HH:mm'));
        }
        if(this.parent().remunerationType()) {
            fd.append('remunerationType', this.parent().remunerationType().toString());
        }

        const branchesResponse = await fetch(url, {
            method: 'POST',
            body: fd
        });

        if(branchesResponse.ok) {
            this.branches(branchesResponse.data.map((model: any) => new HoursBranch(model, this.parent(), this)));
        }
    }
    
    async sendCompanyHours() {
        if(!confirm(`Weet u zeker dat u de uren voor bedrijf ${this.name()} wilt versturen?`)) {
            return;
        }
        
        await this.sendCompanyHoursToUrl(this.parent().options().sendCompanyHoursUrl);
    }

    async sendErrorCompanyHours() {
        if(!confirm(`Weet u zeker dat u de uitval voor bedrijf ${this.name()} wilt versturen?`)) {
            return;
        }
        await this.sendCompanyHoursToUrl(this.parent().options().sendErrorsCompanyUrl);
    }

    async sendCompanyHoursToUrl(url: string) {
        if(this.sending()) {
            return;
        }
        this.sending(true);
        const fd = new FormData();
        fd.append('companyId',this.companyId());
        if(this.parent().startDate()) {
            fd.append('startDate', this.parent().startDate().format('YYYY-MM-DD HH:mm'));
        }
        if(this.parent().endDate()) {
            fd.append('endDate', this.parent().endDate().format('YYYY-MM-DD HH:mm'));
        }
        if(this.parent().remunerationType()) {
            fd.append('remunerationType', this.parent().remunerationType().toString());
        }
        const employeesResponse = await fetch(url, {
            method: 'POST',
            body: fd
        });

        if(employeesResponse.ok) {
            this.parent().removeCompany(this.companyId());
            await this.parent().updateProcessingCount();
        }
    }
    
    removeBranch(id : string) {
        this.branches(this.branches().filter(e => e.branchId() != id));
    }
}

class HoursBranch {
    name : KnockoutObservable<string>;
    branchId : KnockoutObservable<string>;
    clockedUnitCount : KnockoutObservable<number>;
    totalHours : KnockoutObservable<number>;
    employeesCount : KnockoutObservable<number>;
    longestClock : KnockoutObservable<number>;
    shortestClock : KnockoutObservable<number>;
    parent: KnockoutObservable<AfasDashboardModel>;
    company: KnockoutObservable<HoursCompany>;
    employees : KnockoutObservable<HoursEmployee[]>;
    
    totalHoursText: KnockoutComputed<string>;
    shortestLongestText: KnockoutComputed<string>;
    
    sending: KnockoutObservable<boolean>;

    constructor(model: any, parent: AfasDashboardModel, company: HoursCompany) {
        this.parent = ko.observable(parent);
        this.company = ko.observable(company);
        this.name = ko.observable(model.name);
        this.branchId = ko.observable(model.branchId);
        this.clockedUnitCount = ko.observable(model.clockedUnitCount);
        this.employeesCount = ko.observable(model.employeesCount);
        this.totalHours = ko.observable(model.totalHours);
        this.longestClock = ko.observable(model.longestClock);
        this.shortestClock = ko.observable(model.shortestClock);
        this.employees = ko.observable<HoursEmployee[]>([]);

        this.totalHoursText = ko.pureComputed(() => `${this.totalHours().toFixed(2)} uur`);
        this.shortestLongestText = ko.pureComputed(() => `${this.shortestClock().toFixed(2)} / ${this.longestClock().toFixed(2)}`);
        this.sending = ko.observable(false);

    }

    async getEmployees() {
        await this.getEmployeesFromUrl(this.parent().options().getHoursEmployeesUrl)
    }

    async getErrorEmployees() {
        await this.getEmployeesFromUrl(this.parent().options().getErrorEmployeesUrl)
    }

    async getEmployeesFromUrl(url : string) {

        const fd = new FormData();
        fd.append('branchId',this.branchId());
        if(this.parent().startDate()) {
            fd.append('startDate', this.parent().startDate().format('YYYY-MM-DD HH:mm'));
        }
        if(this.parent().endDate()) {
            fd.append('endDate', this.parent().endDate().format('YYYY-MM-DD HH:mm'));
        }
        if(this.parent().remunerationType()) {
            fd.append('remunerationType', this.parent().remunerationType().toString());
        }

        const employeesResponse = await fetch(url, {
            method: 'POST',
            body: fd
        });

        if(employeesResponse.ok) {
            this.employees(employeesResponse.data.map((model: any) => new HoursEmployee(model, this.parent(), this)));
        }
    }

    async sendBranchHours() {
        if(!confirm(`Weet u zeker dat u de uren voor locatie "${this.company().name()} - ${this.name()}" wilt versturen?`)) {
            return;
        }
            
        await this.sendBranchHoursToEmployee(this.parent().options().sendBranchHoursUrl);
    }

    async sendErrorBranchHours() {
        if(!confirm(`Weet u zeker dat u de uitval voor locatie "${this.company().name()} - ${this.name()}" wilt versturen?`)) {
            return;
        }
        await this.sendBranchHoursToEmployee(this.parent().options().sendErrorBranchUrl);
    }

    async sendBranchHoursToEmployee(url: string) {
        if(this.sending()) {
            return;
        }
        this.sending(true);

        const fd = new FormData();
        fd.append('branchId',this.branchId());
        if(this.parent().startDate()) {
            fd.append('startDate', this.parent().startDate().format('YYYY-MM-DD HH:mm'));
        }
        if(this.parent().endDate()) {
            fd.append('endDate', this.parent().endDate().format('YYYY-MM-DD HH:mm'));
        }
        if(this.parent().remunerationType()) {
            fd.append('remunerationType', this.parent().remunerationType().toString());
        }
        const employeesResponse = await fetch(url, {
            method: 'POST',
            body: fd
        });

        if(employeesResponse.ok) {
            this.company().removeBranch(this.branchId());
            await this.parent().updateProcessingCount();
        }
    }
    
    removeEmployee(id : string) {
        this.employees(this.employees().filter(e => e.id() != id));
    }
}


class HoursEmployee extends AfasDashboardBaseEmployee {

    parent: KnockoutObservable<AfasDashboardModel>;
    
    fullName: KnockoutObservable<string>;
    branch: KnockoutObservable<HoursBranch>;
    hours: KnockoutObservable<Hours[]>;
    sending: KnockoutObservable<boolean>;
    longestClock : KnockoutObservable<number>;
    shortestClock : KnockoutObservable<number>;
    
    shortestLongestText: KnockoutObservable<string>;
    
    constructor(model: any, parent: AfasDashboardModel, company: HoursBranch) {
        super(model, parent);
        this.parent = ko.observable(parent);
        
        this.fullName = ko.observable<string>(model.fullName);
        this.branch = ko.observable(company);
        this.hours = ko.observable<Hours[]>([]);
        this.sending = ko.observable(false);
        this.longestClock = ko.observable(model.longestClock);
        this.shortestClock = ko.observable(model.shortestClock);
        
        this.shortestLongestText = ko.pureComputed(() => `${this.shortestClock().toFixed(2)} / ${this.longestClock().toFixed(2)}`);
        
        if(!!model.errorHours) {
            this.hours(model.errorHours.map((h: any) => new Hours(h, this)));
        }
    }

    async getHours() {

        const fd = new FormData();
        fd.append('employeeId',this.id());
        if(this.parent().startDate()) {
            fd.append('startDate', this.parent().startDate().format('YYYY-MM-DD HH:mm'));
        }
        if(this.parent().endDate()) {
            fd.append('endDate', this.parent().endDate().format('YYYY-MM-DD HH:mm'));
        }
        if(this.parent().remunerationType()) {
            fd.append('remunerationType', this.parent().remunerationType().toString());
        }

        const hoursResponse = await fetch(this.parent().options().getEmployeeClockedUnitsUrl, {
            method: 'POST',
            body: fd
        });

        if(hoursResponse.ok) {
            this.hours(hoursResponse.data.map((model: any) => new Hours(model, this)));
        }
    }

    removeClockedUnit(id : string) {
        this.hours(this.hours().filter(e => e.id() != id));
    }

    async sendEmployeeHours() {
        if(!confirm(`Weet u zeker dat u de uren voor medewerker "${this.fullName()}" wilt versturen?`)) {
            return;
        }

        await this.sendEmployeeHoursToUrl(this.parent().options().sendEmployeeHoursUrl);
    }

    async sendErrorEmployeeHours() {
        if(!confirm(`Weet u zeker dat u de uitval voor medewerker "${this.fullName()}" wilt versturen?`)) {
            return;
        }
        
        await this.sendEmployeeHoursToUrl(this.parent().options().sendErrorEmployeeUrl);
    }

    async removeEmployeeHours() {
        if(!confirm(`Weet u zeker dat u de uren voor medewerker "${this.fullName()}" wilt verwijderen?`)) {
            return;
        }
        
        await this.sendEmployeeHoursToUrl(this.parent().options().removeEmployeeHours);
    }

    async removeErrorEmployeeHours() {
        if(!confirm(`Weet u zeker dat u de uitval voor medewerker "${this.fullName()}" wilt verwijderen?`)) {
            return;
        }
        
        await this.sendEmployeeHoursToUrl(this.parent().options().removeErrorEmployeeHours);
    }

    async sendEmployeeHoursToUrl(url: string) {
        if(this.sending()) {
            return;
        }
        this.sending(true);

        const fd = new FormData();
        fd.append('employeeId',this.id());
        fd.append('branchId', this.branch().branchId());
        if(this.parent().startDate()) {
            fd.append('startDate', this.parent().startDate().format('YYYY-MM-DD HH:mm'));
        }
        if(this.parent().endDate()) {
            fd.append('endDate', this.parent().endDate().format('YYYY-MM-DD HH:mm'));
        }
        if(this.parent().remunerationType()) {
            fd.append('remunerationType', this.parent().remunerationType().toString());
        }

        const employeesResponse = await fetch(url, {
            method: 'POST',
            body: fd
        });

        if(employeesResponse.ok) {
            this.branch().removeEmployee(this.id());
            await this.parent().updateProcessingCount();
        }
    }
}

class Hours {
    id : KnockoutObservable<string>;
    date : KnockoutObservable<string>;
    finished : KnockoutObservable<string>;
    branchName : KnockoutObservable<string>;
    workedHours : KnockoutObservable<string>;
    weekNumber: KnockoutObservable<string>;
    monthNumber: KnockoutObservable<string>;
    eventName: KnockoutObservable<string>;
    
    formattedDate: KnockoutObservable<string>;
    formattedTimes: KnockoutObservable<string>;
    formattedPeriodNumber: KnockoutComputed<string>;
    
    employee: KnockoutObservable<HoursEmployee>;
    sending: KnockoutObservable<boolean>;
    message: KnockoutObservable<string>;
    
    constructor(model: any, employee: HoursEmployee) {
        this.employee = ko.observable(employee);
        this.id = ko.observable(model.id);
        this.date = ko.observable(model.date);
        this.weekNumber = ko.observable(model.weekNumber);
        this.monthNumber = ko.observable(model.monthNumber);
        this.eventName = ko.observable(model.eventName);
        this.finished = ko.observable(model.finished);
        this.branchName = ko.observable(model.branchName);
        this.workedHours = ko.observable(model.workedHours);
        this.sending = ko.observable(false);
        this.message = ko.observable(model.message);
        
        this.formattedDate = ko.pureComputed(() => moment(this.date()).format("DD-MM-YYYY"));
        this.formattedTimes = ko.pureComputed(() =>
            `${(moment(this.date()).format("HH:mm"))} tot ${moment(this.finished()).format("HH:mm")}`)
        this.formattedPeriodNumber = ko.pureComputed(() => this.employee().parent().remunerationType() == 2 ? `mnd ${this.monthNumber()}` : `wk ${this.weekNumber()}`)
    }

    async sendClockedUnit() {

        if(this.sending()) {
            return;
        }
        this.sending(true);
        
        const employeesResponse = await fetch(this.employee().parent().options().sendClockedUnit + this.id(), {
            method: 'GET'
        });

        if(employeesResponse.ok) {
            this.employee().removeClockedUnit(this.id());
        }
    }

    async sendErrorClockedUnit() {

        if(this.sending()) {
            return;
        }
        this.sending(true);
        
        const employeesResponse = await fetch(this.employee().parent().options().sendErrorClockedUnit + this.id(), {
            method: 'GET'
        });

        if(employeesResponse.ok) {
            this.employee().removeClockedUnit(this.id());
        }
    }

    async removeClockedUnit() {

        if(this.sending()) {
            return;
        }
        this.sending(true);

        const employeesResponse = await fetch(this.employee().parent().options().removeClockedUnit + this.id(), {
            method: 'GET'
        });

        if(employeesResponse.ok) {
            this.employee().removeClockedUnit(this.id());
        }
    }
}


export class AfasDashboardModel {

    loadingHours: KnockoutObservable<boolean>;
    loadingErrors: KnockoutObservable<boolean>;
    
    hoursCompanies: KnockoutObservable<HoursCompany[]>;
    errorCompanies: KnockoutObservable<HoursCompany[]>;

    startDate: KnockoutObservable<moment.Moment>;
    endDate: KnockoutObservable<moment.Moment>;

    remunerationType: KnockoutObservable<number>;
    processingCount: KnockoutObservable<number>;
    statusText: KnockoutComputed<string>;
    options: KnockoutObservable<AfasDashboardOptions>;
    search: KnockoutObservable<string>;
    
    filteredHoursCompanies: KnockoutComputed<HoursCompany[]>;
    filteredErrorCompanies: KnockoutComputed<HoursCompany[]>;
    
    constructor(options: AfasDashboardOptions) {
        this.loadingHours = ko.observable(false);
        this.loadingErrors = ko.observable(false);
        this.hoursCompanies = ko.observable([]);
        this.errorCompanies = ko.observable([]);
        this.options = ko.observable(options);
        this.search = ko.observable('');
        this.startDate = ko.observable<moment.Moment>(moment().subtract(2, 'months'));
        this.endDate = ko.observable<moment.Moment>(moment().add(1, 'months'));
        this.remunerationType = ko.observable<number>();
        this.processingCount = ko.observable(0);
        this.statusText = ko.pureComputed(() => `${this.processingCount()} urenregels nog in verwerking.`)

        this.filteredHoursCompanies = ko.pureComputed(() => {
            if(this.search().length > 0) {
                return this.hoursCompanies().filter(c => c.name().toLowerCase().includes(this.search().toLowerCase()));
            }
            return this.hoursCompanies();
        });

        this.filteredErrorCompanies = ko.pureComputed(() => {
            if(this.search().length > 0) {
                return this.errorCompanies().filter(c => c.name().toLowerCase().includes(this.search().toLowerCase()));
            }
            return this.errorCompanies();
        });
    }

    applyFilters() {
        Promise.all([
            this.getHoursCompanies(),
            this.getErrorCompanies(),
            this.updateProcessingCount()
        ]);
    }
    
    updateSentHours() {
        this.updateProcessingCount();
        this.getErrorCompanies();
    }
    
    async updateProcessingCount() {
        const response = await fetch(this.options().getProcessingCountUrl, {
            method: 'GET'
        });

        if(response.ok) {
            this.processingCount(response.data);
        }
    }
 
    async getHoursCompanies() {
        const fd = new FormData();
        if(this.startDate()) {
            fd.append('startDate', this.startDate().format('YYYY-MM-DD HH:mm'));
        }
        if(this.endDate()) {
            fd.append('endDate', this.endDate().format('YYYY-MM-DD HH:mm'));
        }
        if(this.remunerationType()) {
            fd.append('remunerationType', this.remunerationType().toString());
        }
        if(this.search()) {
            fd.append('companySearchTerm', this.search());
        }
        
        this.loadingHours(true);

        try {
            const response = await fetch(this.options().getHoursCompaniesUrl, {
                method: 'POST',
                body: fd
            });
            
            if(response.ok) {
                this.hoursCompanies(response.data.map((model: any) => new HoursCompany(model, this)));
            }
        } finally {
            this.loadingHours(false);
        }
    }

    removeCompany(id : string) {
        this.hoursCompanies(this.hoursCompanies().filter(c => c.companyId() != id));
    }

    removeErrorCompany(id : string) {
        this.errorCompanies(this.errorCompanies().filter(c => c.companyId() != id));
    }

    async getErrorCompanies() {
        const fd = new FormData();
        if(this.startDate()) {
            fd.append('startDate', this.startDate().format('YYYY-MM-DD HH:mm'));
        }
        if(this.endDate()) {
            fd.append('endDate', this.endDate().format('YYYY-MM-DD HH:mm'));
        }
        if(this.remunerationType()) {
            fd.append('remunerationType', this.remunerationType().toString());
        }
        if(this.search()) {
            fd.append('companySearchTerm', this.search());
        }

        this.loadingErrors(true);

        try {
            const response = await fetch(this.options().getErrorCompaniesUrl, {
                method: 'POST',
                body: fd
            });
    
            if(response.ok) {
                this.errorCompanies(response.data.map((model: any) => new HoursCompany(model, this)));
            }
        } finally {
            this.loadingErrors(false);
        }
    }
}

class AfasDashboardOptions {
    getHoursCompaniesUrl: string;
    getHoursEmployeesUrl: string;
    getHoursBranchesUrl: string;
    getEmployeeClockedUnitsUrl: string;
    getErrorCompaniesUrl: string;
    getErrorBranchesUrl: string;
    getErrorEmployeesUrl: string;
    sendCompanyHoursUrl: string;
    sendBranchHoursUrl: string;
    sendEmployeeHoursUrl: string;
    sendClockedUnit:string;
    removeClockedUnit:string;
    removeEmployeeHours: string;
    removeErrorEmployeeHours: string;
    sendErrorsCompanyUrl: string;
    sendErrorBranchUrl: string;
    sendErrorEmployeeUrl: string;
    sendErrorClockedUnit:string;
    getProcessingCountUrl: string;
}

